import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLiveData } from "../services/slices/liveDataSlice";
import { clearAlarms, setAlarms } from "../services/slices/amsSlice";
import { setSliderTime } from "../services/slices/playBackSlice";

const useSimulationData = (processLiveData, liveFlag) => {
  const [simulationPlay, setSimulationPlay] = useState(true);
  const [simulationData, setSimulationData] = useState({});
  const [simulationAlarmData, setSimulationAlarmData] = useState({});
  const [sliderValue, setSliderValue] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSliding = useRef(false);
  const dataFlag = useRef(true);
  const [loadingFlag, setLoadingFlag] = useState(false);

  const dispatch = useDispatch();

  const isPlaybackMode = useSelector((s) => s.appConfig?.isPlaybackMode);

  /* FIND MATCHING DATA FOR MINUTE AND SECOND */

  const findMatchingKey = useCallback((keys, targetTime) => {
    const targetDate = new Date(targetTime);
    const targetMinute = targetDate.getMinutes();
    const targetSecond = targetDate.getSeconds();

    for (const key of keys) {
      const date = new Date(key);
      if (
        date.getMinutes() === targetMinute &&
        date.getSeconds() === targetSecond
      ) {
        return key;
      }
    }
    return null;
  }, []);

  /* HANDLE SIMULATION DATA */

  const getCombinedData = useCallback(
    (keys) => {
      return keys.reduce((accumulator, key) => {
        return { ...accumulator, ...simulationData[key] };
      }, {});
    },
    [simulationData]
  );

  /* HANDLE SIMULATION SENSORS DATA */

  useEffect(() => {
    if (!isPlaybackMode) return;
    if (!liveFlag.current && !isSliding.current) {
      const normalized = Math.floor(sliderValue / 1000);

      dispatch(setSliderTime(normalized));

      const keys = Object.keys(simulationData);
      let matchingKey = findMatchingKey(keys, sliderValue);

      if (!matchingKey) {
        const previousSliderValue = new Date(sliderValue - 1000);
        matchingKey = findMatchingKey(keys, previousSliderValue);
        if (!matchingKey) {
          dispatch(setAlarms({}));
          dispatch(setLiveData({}));
          return;
        }
      }

      const matchingDate = new Date(matchingKey);
      const matchingISODate = matchingDate
        .toISOString()
        .replace(/\.\d+Z$/, "Z");
      const matchingKeys = keys.filter((key) =>
        key.startsWith(matchingISODate.slice(0, -1))
      );
      const combinedData = getCombinedData(matchingKeys);
      if (Object.keys(combinedData).length) {
        processLiveData(combinedData);
      }
    }
  }, [simulationData, sliderValue, processLiveData]);

  /* HANDLE SIMULATION ALARM DATA */

  useEffect(() => {
    if (!isPlaybackMode) return;
    const hasAlarms =
      simulationAlarmData &&
      Object.values(simulationAlarmData).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );

    if (!hasAlarms) {
      console.log("No active alarms");
      dispatch(clearAlarms());
      return;
    }

    const now = new Date(sliderValue);
    const activeAlarms = {};
    Object.entries(simulationAlarmData).forEach(([serial, alarms]) => {
      if (!Array.isArray(alarms) || alarms.length === 0) return;
      const activeForSerial = alarms
        .map((e) => ({ ...e }))
        .filter((e) => {
          const on = new Date(e.onTs);
          const ackTs = new Date(e.ackTs);
          const off = e.offTs ? new Date(e.offTs) : null;
          const offTsValid = e?.offTsValid;
          if (ackTs > now) {
            e.ackTsValid = false;
          }

          if (offTsValid) {
            // off => old
            if (now >= on && now <= off) {
              return true;
            }
          } else {
            if (now >= on) {
              return true;
            }
          }
          return false;
        });

      if (activeForSerial.length > 0) {
        activeAlarms[serial] = activeForSerial;
      }
    });

    dispatch(setAlarms(activeAlarms));
  }, [simulationAlarmData, sliderValue]);

  return {
    simulationPlay,
    setSimulationPlay,
    simulationData,
    simulationAlarmData,
    setSimulationData,
    setSimulationAlarmData,
    sliderValue,
    setSliderValue,
    isSliding,
    dataFlag,
    loadingFlag,
    setLoadingFlag,
    isModalOpen,
    setIsModalOpen,
  };
};

export default useSimulationData;
